import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppConfirmDialog from "../../design-system/components/AppConfirmDialog";
import AppDialog from "../../design-system/components/AppDialog";
import AppDialogBody from "../../design-system/components/AppDialogBody";
import AppDialogHeader from "../../design-system/components/AppDialogHeader";
import AppTable from "../../design-system/components/AppTable";
import AppTablePane from "../../design-system/components/AppTablePane";
import AppTooltip from "../../design-system/components/AppTooltip";
import {
  IconAdd,
  IconAttachment,
  IconDelete,
  IconPreview,
} from "../../design-system/icons";
import { API_BASE_URL } from "../../config";

const formatUploadedAt = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export default function UserAttachmentsDialog({
  open,
  user,
  canUpload = false,
  onClose,
  onCountChange,
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [error, setError] = useState("");

  const token = localStorage.getItem("session_token");
  const userName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");

  const loadDocuments = useCallback(async () => {
    if (!open || !user?.id) return;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API_BASE_URL}/api/web/team/users/${user.id}/documents`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const nextDocuments = Array.isArray(response.data?.documents)
        ? response.data.documents
        : [];
      setDocuments(nextDocuments);
      onCountChange?.(user.id, nextDocuments.length);
    } catch {
      setDocuments([]);
      setError(t("team.attachments.load_error"));
    } finally {
      setLoading(false);
    }
  }, [onCountChange, open, t, token, user?.id]);

  useEffect(() => {
    if (open) {
      loadDocuments();
    } else {
      setDocuments([]);
      setDocumentToDelete(null);
      setError("");
    }
  }, [loadDocuments, open]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !canUpload || !user?.id) return;

    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API_BASE_URL}/api/web/team/users/${user.id}/documents`, formData, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "multipart/form-data",
        },
      });
      await loadDocuments();
    } catch (uploadError) {
      setError(uploadError?.response?.data?.message || t("team.attachments.upload_error"));
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (document) => {
    const previewWindow = window.open("", "_blank");
    if (previewWindow) previewWindow.opener = null;
    setError("");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/web/team/users/${user.id}/documents/${document.id}`,
        {
          responseType: "blob",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      const url = URL.createObjectURL(response.data);
      if (previewWindow) {
        previewWindow.location.href = url;
      } else {
        const link = window.document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.click();
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      previewWindow?.close();
      setError(t("team.attachments.view_error"));
    }
  };

  const handleDelete = async () => {
    if (!documentToDelete || !user?.id) return;

    setError("");
    try {
      await axios.delete(
        `${API_BASE_URL}/api/web/team/users/${user.id}/documents/${documentToDelete.id}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      setDocumentToDelete(null);
      await loadDocuments();
    } catch (deleteError) {
      setError(deleteError?.response?.data?.message || t("team.attachments.delete_error"));
    }
  };

  const columns = [
    {
      key: "file_name",
      label: t("team.attachments.document"),
      minWidth: 260,
      grow: 2,
    },
    {
      key: "uploaded_at",
      label: t("team.attachments.added_at"),
      minWidth: 150,
      grow: 1,
      render: formatUploadedAt,
    },
    {
      key: "preview",
      label: "",
      width: 64,
      grow: 0,
      align: "center",
      controlKind: "action",
      sortable: false,
      render: (_value, row) => (
        <AppTooltip content={t("team.attachments.view")}>
          <AppButton
            variant="neutral"
            size="sm"
            iconOnly
            aria-label={t("team.attachments.view")}
            onClick={() => handleView(row)}
          >
            <IconPreview />
          </AppButton>
        </AppTooltip>
      ),
    },
    canUpload && {
      key: "delete",
      label: "",
      width: 64,
      grow: 0,
      align: "center",
      controlKind: "action",
      sortable: false,
      render: (_value, row) => (
        <AppTooltip content={t("team.attachments.delete")}>
          <AppButton
            variant="error"
            size="sm"
            iconOnly
            aria-label={t("team.attachments.delete")}
            onClick={() => setDocumentToDelete(row)}
          >
            <IconDelete />
          </AppButton>
        </AppTooltip>
      ),
    },
  ].filter(Boolean);

  return (
    <>
      <AppDialog open={open} onClose={onClose} size="md">
        <AppDialogHeader
          title={t("team.attachments.title", { name: userName || t("team.title") })}
          visual={<IconAttachment />}
          action={canUpload ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                hidden
                onChange={handleUpload}
              />
              <AppButton
                variant="neutral"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <IconAdd />
                {uploading ? t("team.attachments.uploading") : t("team.attachments.add")}
              </AppButton>
            </>
          ) : null}
        />
        <AppDialogBody>
          {error ? <AppCallout variant="error" description={error} /> : null}
          <AppTablePane className="team-attachments-dialog__table">
            <AppTable
              rows={documents}
              columns={columns}
              getRowId={(row) => row.id}
              loading={loading}
              density="compact"
              showFooter={false}
              emptyState={t("team.attachments.empty")}
            />
          </AppTablePane>
        </AppDialogBody>
      </AppDialog>
      <AppConfirmDialog
        open={Boolean(documentToDelete)}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleDelete}
        title={t("team.attachments.delete_title")}
        description={t("team.attachments.delete_question", {
          name: documentToDelete?.file_name || "",
        })}
        cancelLabel={t("button.cancel")}
        confirmLabel={t("button.delete")}
        confirmTone="error"
        confirmIcon={<IconDelete />}
      />
    </>
  );
}
